import { gql } from "lib/apolloClient";
import {
  assessmentFragment,
  assessmentSubjectFragment,
  fieldListFragments,
  unitPlanFragment,
  plannerElementSetFragment,
  pypPlannerElementSetFragment,
  genericPlannerElementSetFragment,
} from "./AssessmentFragments";

import {
  assessmentPlannerElementFragments,
  assessmentBenchmarkFragment,
} from "AppComponents/AssessmentLearningGoals/module/AssessmentLearningGoalsFragments";

export const getFieldListQuery = gql`
  query getFieldList(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $templateFieldFilters: TemplateFieldFilter
    $templateFieldPlannerElementSetFilters: TemplateFieldPlannerElementSetFilters
  ) {
    node(id: $id, type: $type) {
      ... on IbPypCurriculumProgram {
        id
        templateField(filters: $templateFieldFilters) {
          ...templateFieldConnectionItem
          __typename
        }
        __typename
      }
      ... on IbMypCurriculumProgram {
        id
        templateField(filters: $templateFieldFilters) {
          ...templateFieldConnectionItem
          __typename
        }
        __typename
      }
      ... on UBDCurriculumProgram {
        id
        templateField(filters: $templateFieldFilters) {
          ...templateFieldConnectionItem
          __typename
        }
        __typename
      }
      ... on IbDpCurriculumProgram {
        id
        templateField(filters: $templateFieldFilters) {
          ...templateFieldConnectionItem
          __typename
        }
        __typename
      }
      __typename
    }
  }
  ${fieldListFragments.templateFieldConnectionItem}
`;

export const getAssessmentQuery = gql`
  query getAssessment(
    $id: ID!
    $collaboratorsFilter: CollaboratorFilters
    $libraryBannerFilters: LibraryPremiumBannerFilters
  ) {
    node(id: $id, type: ASSESSMENT) {
      id
      ... on Assessment {
        ...assessmentItem
      }
    }
  }
  ${assessmentFragment.assessmentDetails}
`;

export const getAssessmentLinkedAssignmentsQuery = gql`
  query getAssessmentLinkedAssignments($id: ID!) {
    node(id: $id, type: ASSESSMENT) {
      id
      ... on Assessment {
        id
        type
        academicTerm {
          id
          label
          lockingStatus
        }
        assessmentType {
          id
          value
        }
        assignments {
          edges {
            id
            state {
              state
            }
          }
          userAssignmentEdges {
            id
          }
        }
        lockingInfo {
          isLocked
        }
        isSyncedWithAllTeacherCourseV2 {
          status
          isConflictExists
          courseSyncStatus {
            course {
              id
              title
            }
            status
          }
        }
        learningCourse {
          id
          type
        }
      }
    }
  }
`;

export const getAssessmentPreviewQuery = gql`
  query getAssessmentPreview(
    $id: ID!
    $userArchivedFilters: UserArchivedFilters
    $collaboratorsFilter: CollaboratorFilters
  ) {
    node(id: $id, type: ASSESSMENT) {
      id
      ... on Assessment {
        ...assessmentItem
      }
    }
  }
  ${assessmentFragment.assessmentPreviewDetails}
`;

export const getAssessmentOfUnitQuery = gql`
  query getUnitPlan($id: ID!, $filters: resourceLibraryFilter) {
    node(id: $id, type: UNIT_PLAN) {
      id
      ... on UnitPlan {
        id
        resourceLibrary(filters: $filters) {
          edges {
            type
            node {
              ...assessmentItem
            }
          }
        }
      }
    }
  }
  ${assessmentFragment.assessmentSelectModalFeed}
`;

export const getParentAssesmentQuery = gql`
  query getParentAssesment(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $searchText: String
    $grades: [ID!]
    $learningCourseIds: [ID!]
    $units: [ID!]
    $academicYears: [ID!]
    $curriculumProgramIds: [ID!]
    $after: String
    $first: Int
    $excludeSnPEvidenceFilters: ExcludeSnPEvidenceFilters
  ) {
    node(id: $id, type: $type) {
      id
      ... on Organization {
        assessments(
          first: $first
          after: $after
          filters: {
            searchText: $searchText
            grades: $grades
            learningCourses: $learningCourseIds
            academicYears: $academicYears
            units: $units
            curriculumProgramIds: $curriculumProgramIds
            excludeSnPEvidenceFilters: $excludeSnPEvidenceFilters
          }
        ) {
          totalCount
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              ...assessmentItem
            }
          }
        }
      }
    }
  }
  ${assessmentFragment.assessmentSelectModalFeed}
`;

export const getGradesTemplatesQuery = gql`
  query getGradesTemplates($id: ID!, $gradeTemplateFilters: TemplateFilters) {
    node(id: $id, type: GRADE) {
      id
      ... on Grade {
        plannerTemplates(filters: $gradeTemplateFilters) {
          id
          type
          assessmentType
          curriculumProgram {
            id
          }
        }
      }
    }
  }
`;

export const getUnitPlanDetailsQuery = gql`
  query getUnitPlanDetails(
    $id: ID!
    $assessmentLibraryFilters: resourceLibraryFilter!
    $leLibraryFilters: resourceLibraryFilter!
    $collaboratorsFilter: CollaboratorFilters
  ) {
    node(id: $id, type: UNIT_PLAN) {
      id
      ... on UnitPlan {
        id
        ...unitPlanItem
      }
    }
  }
  ${unitPlanFragment.unitPlanDetails}
`;

export const getFlatUnitPlanFeedForAssessmentQuery = gql`
  query getFlatUnitPlanFeedForAssessment(
    $id: ID!
    $type: ENTITY_TYPE_ENUM! = COURSE
    $first: Int
    $after: String
    $orderBy: UnitPlanOrderByEnum
    $orderByDirection: OrderByDirectionEnum
    $filters: UnitPlansV2FeedFilters
    $learningCourseUnitPlanFilters: LearningCourseUnitPlanFilter
  ) {
    node(id: $id, type: $type) {
      id
      ... on Course {
        id
        flatUnitPlans(
          first: $first
          after: $after
          orderBy: $orderBy
          orderByDirection: $orderByDirection
          filters: $filters
        ) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              ...unitPlanBasicDetailsItem
            }
          }
        }
      }

      ... on LearningCourse {
        id
        flatUnitPlans: unitPlans(
          first: $first
          after: $after
          orderBy: $orderBy
          orderByDirection: $orderByDirection
          filters: $learningCourseUnitPlanFilters
        ) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              ...unitPlanBasicDetailsItem
            }
          }
        }
      }
    }
  }
  ${unitPlanFragment.unitPlanBasicDetails}
`;

export const getAssessmentBasicDetailsQuery = gql`
  query getAssessmentBasicDetails($id: ID!) {
    node(id: $id, type: ASSESSMENT) {
      id
      ... on Assessment {
        id
        title {
          id
          value
        }
        fields(uids: ["assessmentType"]) {
          id
          uid
          value
        }
        isDeleted
      }
    }
  }
`;

export const getAssessmentCourseDetailsQuery = gql`
  query getAssessmentCourseDetails($id: ID!) {
    node(id: $id, type: COURSE) {
      id
      ... on Course {
        id
        title
        grades {
          id
        }
        primaryGrade {
          id
          subjects {
            ...assessmentSubjectItem
          }
        }
        subjects {
          ...assessmentSubjectItem
        }
        genericTags(filters: { types: [SUBJECT_LEVEL] }) {
          id
          label
        }
      }
    }
  }
  ${assessmentSubjectFragment}
`;

export const getAssessmentLearningCourseDetailsQuery = gql`
  query getAssessmentLearningCourseDetails($id: ID!) {
    node(id: $id, type: LEARNING_COURSE) {
      id
      ... on LearningCourse {
        id
        title
        grades {
          id
        }
        primaryGrade {
          id
          subjects {
            ...assessmentSubjectItem
          }
        }
        subjects {
          ...assessmentSubjectItem
        }
        subjectLevelMap {
          subject {
            id
            name
          }
          associatedLevels {
            id
            label
            abbreviation
            reportDisplayName
          }
        }
        associatedLevels {
          id
          label
          abbreviation
          reportDisplayName
        }
      }
    }
  }
  ${assessmentSubjectFragment}
`;

export const getAIPromptsBasicDetailsQuery = gql`
  query getAIPromptsBasicDetails($filters: PromptFilters!) {
    ai {
      prompts(filters: $filters) {
        id
        action
        displayText
        promptType
      }
    }
  }
`;

export const getRecommendedPhaseOfSubjectsQuery = gql`
  query getRecommendedPhaseOfSubjects($ids: [ID!]) {
    multiNode(ids: $ids, type: SUBJECT) {
      ... on Subject {
        id
        recommendedPhase {
          phaseId
          gradeId
        }
      }
    }
  }
`;

export const getPlannerElementSetsOfCurriculumQuery = gql`
  query getPlannerElementSetsOfCurriculum(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $plannerElementNodeSetFilters: PlannerElementNodeSetFilters
    $filters: PlannerElementNodeFilters
  ) {
    node(id: $id, type: $type) {
      id
      ... on IbMypCurriculumProgram {
        id
        plannerElementNodeSets(filters: $plannerElementNodeSetFilters) {
          edges {
            node {
              id
              title
              type
              plannerElementSet {
                id
                type
                label
              }
              plannerElementNodeSetWrapperV2 {
                ...plannerElementSetWithNodesItem
              }
            }
          }
        }
      }
      ... on UBDCurriculumProgram {
        id
        plannerElementNodeSets(filters: $plannerElementNodeSetFilters) {
          edges {
            node {
              id
              title
              type
              plannerElementSet {
                id
                type
                label
              }
              plannerElementNodeSetWrapperV2 {
                ...plannerElementSetWithNodesItem
              }
            }
          }
        }
      }
      ... on IbDpCurriculumProgram {
        id
        plannerElementNodeSets(filters: $plannerElementNodeSetFilters) {
          edges {
            node {
              id
              title
              type
              plannerElementSet {
                id
                type
                label
              }
              plannerElementNodeSetWrapperV2 {
                ...plannerElementSetWithNodesItem
              }
            }
          }
        }
      }
    }
  }

  ${plannerElementSetFragment.plannerElementSetWithNodes}
`;

export const getPypPlannerElementNodesOfCurriculumQuery = gql`
  query getPypPlannerElementNodes(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $plannerElementNodeSetFilters: PlannerElementNodeSetFilters
    $plannerElementATLFilters: PlannerElementNodeFilters
    $plannerElementNonAtlFilters: PlannerElementNodeFilters
  ) {
    node(id: $id, type: $type) {
      id
      ... on IbPypCurriculumProgram {
        id
        plannerElementNodeSets(filters: $plannerElementNodeSetFilters) {
          edges {
            node {
              id
              title
              type
              plannerElementSet {
                id
                type
                label
              }
              plannerElementNodeSetWrapperV2 {
                ...pypPlannerElementSetWithNodesItem
              }
            }
          }
        }
      }
    }
  }
  ${genericPlannerElementSetFragment.plannerElementSetWithNodes}
`;

export const getBenchmarkSetOfCurriculumQuery = gql`
  query getBenchmarkSetOfCurriculum(
    $curriculumId: ID!
    $type: ENTITY_TYPE_ENUM!
    $plannerElementNodeSetFilters: PlannerElementNodeSetFilters
    $benchmarkNodeFilters: PlannerElementNodeFilters
  ) {
    node(id: $curriculumId, type: $type) {
      id
      ... on IbPypCurriculumProgram {
        id
        plannerElementNodeSets(filters: $plannerElementNodeSetFilters) {
          edges {
            node {
              id
              title
              type
              plannerElementSet {
                id
                type
                label
              }
              plannerElementNodeSetWrapperV2 {
                ...PlannerBenchmarkSetItem
              }
            }
          }
        }
      }
    }
  }
  ${pypPlannerElementSetFragment.benchmarkSet}
`;

export const getCurriculumStandardsQuery = gql`
  query getCurriculumStandards(
    $id: ID!
    $plannerElementNodeSetFilters: PlannerElementNodeSetFilters
    $plannerElementNodeFilters: PlannerElementNodeFilters
    $isPYPCurriculum: Boolean! = false
    $orderBy: OrderByEnum
    $orderByDirection: OrderByDirectionEnum
  ) {
    node(id: $id, type: CURRICULUM_PROGRAM) {
      id
      ... on CurriculumProgram {
        id
        plannerElementNodeSets(
          orderBy: $orderBy
          orderByDirection: $orderByDirection
          filters: $plannerElementNodeSetFilters
        ) {
          edges {
            node {
              id
              title
              plannerElementNodeSetWrapperV2 {
                ... on PlannerElementNodeSetWrapper {
                  publishedData(filters: $plannerElementNodeFilters)
                    @skip(if: $isPYPCurriculum) {
                    edges {
                      node {
                        ...plannerElementNodeItem
                      }
                    }
                  }
                }
                ... on PlannerBenchmarkSetWrapper {
                  publishedBenchmarks: publishedData(
                    filters: $plannerElementNodeFilters
                  ) @include(if: $isPYPCurriculum) {
                    edges {
                      node {
                        ...benchmarkItem
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${assessmentPlannerElementFragments.plannerElementNode}
  ${assessmentBenchmarkFragment.benchmark}
`;
